import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { products } from '../data/mockData';
import { FiSearch, FiAlertCircle, FiCheckCircle, FiInfo, FiFilter } from 'react-icons/fi';

const ProductSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('ALL');

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.brand.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          product.product.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'ALL' || product.status === filter;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'RECALLED': return <span className="text-alert font-black text-[10px] uppercase tracking-widest flex items-center gap-1.5"><FiAlertCircle /> RECALLED</span>;
      case 'SAFE': return <span className="text-primary-light font-black text-[10px] uppercase tracking-widest flex items-center gap-1.5"><FiCheckCircle /> PASS</span>;
      case 'WARNING': return <span className="text-accent font-black text-[10px] uppercase tracking-widest flex items-center gap-1.5"><FiInfo /> WARNING</span>;
      default: return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Safety Database | Moringa Alert</title>
      </Helmet>
      
      <div className="bg-surface min-h-screen pb-24">
        {/* Header */}
        <div className="bg-primary-dark text-white py-24 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-primary-light/5 skew-x-12 transform translate-x-1/4"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <span className="text-primary-light font-black text-xs uppercase tracking-[0.4em] mb-4 block">Central Registry</span>
              <h1 className="text-5xl lg:text-7xl font-serif font-black mb-6">Product Safety Database</h1>
              <p className="text-gray-400 text-xl leading-relaxed mb-10 max-w-xl">
                Independent purity verification and recall tracking for global botanical brands.
              </p>
              
              <div className="relative group">
                <FiSearch className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400 text-2xl group-focus-within:text-primary-light transition-colors" />
                <input 
                  type="text" 
                  placeholder="Search brand, product, or lot number..." 
                  className="w-full pl-16 pr-8 py-6 bg-white/5 border border-white/10 rounded-sm text-white focus:bg-white focus:text-gray-900 focus:outline-none transition-all placeholder:text-gray-500 shadow-2xl"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="container mx-auto px-4 -mt-10 relative z-20">
          <div className="bg-white rounded-sm shadow-journal border border-gray-100 overflow-hidden">
            <div className="p-8 border-b border-gray-50 flex flex-wrap items-center justify-between gap-6">
              <div className="flex gap-4 overflow-x-auto pb-2">
                {['ALL', 'SAFE', 'WARNING', 'RECALLED'].map(f => (
                  <button 
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                      filter === f ? 'bg-primary-dark text-white' : 'text-gray-400 hover:text-primary'
                    }`}
                  >
                    {f === 'ALL' ? 'All Records' : f}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
                <FiFilter /> showing {filteredProducts.length} entries
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-surface border-b border-gray-100">
                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Manufacturer / Product</th>
                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Audit Status</th>
                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Rating</th>
                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Last Verified</th>
                    <th className="px-8 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Compliance Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length > 0 ? filteredProducts.map((p) => (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-surface transition-colors group">
                      <td className="px-8 py-8">
                        <div className="font-serif font-bold text-gray-900 text-lg group-hover:text-primary transition-colors">{p.brand}</div>
                        <div className="text-xs text-gray-400 font-medium mt-1 uppercase tracking-wider">{p.product}</div>
                      </td>
                      <td className="px-8 py-8">
                        {getStatusBadge(p.status)}
                      </td>
                      <td className="px-8 py-8">
                        <span className={`w-10 h-10 flex items-center justify-center font-black rounded-full text-sm border-2 ${
                          p.safetyRating === 'A' ? 'border-primary-light text-primary-light bg-primary-light/5' : 
                          p.safetyRating === 'F' ? 'border-alert text-alert bg-alert/5' : 'border-accent text-accent bg-accent/5'
                        }`}>
                          {p.safetyRating}
                        </span>
                      </td>
                      <td className="px-8 py-8 text-xs font-bold text-gray-500 uppercase tracking-widest">{p.lastTested}</td>
                      <td className="px-8 py-8 text-sm text-gray-600 max-w-xs font-medium leading-relaxed">{p.notes}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="px-8 py-20 text-center text-gray-400 font-serif italic text-xl">
                        No official records match your inquiry.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductSearch;